import {
  AlertOctagon, Clock, WifiOff, ShieldAlert, Timer, Loader2,
} from "lucide-react";
import type { ErrorKind } from "@/features/employeeTransfer/types/employeeTransfer.types";
import { H2 } from "@/shared/ui/typographies/h2";
import { P } from "@/shared/ui/typographies/paragraph";

type ErrorScreenProps = {
  errorKind: ErrorKind
  onResend?: () => void
  resendRequested?: boolean
  isMutating?: boolean
};

const ERROR_CONTENT: Record<ErrorKind, {
  icon: typeof AlertOctagon
  title: string
  description: string
  supportText: string
}> = {
  invalid: {
    icon: AlertOctagon,
    title: "Ce lien de transfert est invalide",
    description: "Le lien que vous avez utilisé n'est pas valide. Vérifiez que vous avez bien copié l'intégralité du lien depuis votre email.",
    supportText: "Si le problème persiste, contactez votre CSE.",
  },
  expired: {
    icon: Clock,
    title: "Ce lien de transfert a expiré",
    description: "Ce lien n'est plus valide. Vous pouvez demander l'envoi d'un nouveau lien par email.",
    supportText: "Les liens de transfert expirent après 2 jours.",
  },
  lockout: {
    icon: ShieldAlert,
    title: "Trop de tentatives de vérification",
    description: "Pour votre sécurité, veuillez réessayer dans 15 minutes ou contactez votre support.",
    supportText: "Cette limitation protège votre compte.",
  },
  network: {
    icon: WifiOff,
    title: "Une erreur est survenue",
    description: "Impossible de contacter le serveur. Vérifiez votre connexion et réessayez.",
    supportText: "Si le problème persiste, contactez le support technique.",
  },
  rate_limit_exceeded: {
    icon: Timer,
    title: "Trop de demandes de renvoi",
    description: "Vous avez demandé trop de renvois récemment. Veuillez patienter avant de réessayer.",
    supportText: "Contactez votre CSE si vous ne recevez pas l'email.",
  },
};

export const ErrorScreen = ({
  errorKind, onResend, resendRequested, isMutating,
}: ErrorScreenProps) => {
  const {
    icon: Icon, title, description, supportText,
  } = ERROR_CONTENT[errorKind];

  return (
    <div className="overflow-hidden rounded-xl" aria-live="polite">
      <div className="bg-red-50/80 px-6 py-8 dark:bg-red-950/20">
        <div className="flex flex-col items-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
            <Icon size={28} className="text-red-600 dark:text-red-400" />
          </div>
          <H2 className="mb-2 text-xl font-bold text-gray-900 dark:text-white">
            {title}
          </H2>
          <P size="sm" color="base" className="text-pretty tracking-wider">
            {description}
          </P>
        </div>
      </div>

      <div className="px-6 pb-2 pt-4">
        {errorKind === "expired" && onResend && !resendRequested && (
          <button
            type="button"
            onClick={onResend}
            disabled={isMutating}
            className="mb-3 w-full rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50 dark:bg-teal-700 dark:hover:bg-teal-600"
          >
            {isMutating
              ? (
                <>
                  <Loader2 size={16} className="mr-2 inline animate-spin" />
                  Envoi en cours…
                </>
              )
              : "Recevoir un nouveau lien"}
          </button>
        )}
        {errorKind === "expired" && resendRequested && (
          <P size="sm" color="base" className="mb-3 text-center font-medium text-teal-700 dark:text-teal-400">
            Un nouvel email de transfert a été envoyé.
          </P>
        )}
        <P size="sm" color="base" className="text-pretty tracking-wider text-center text-gray-900 dark:text-gray-500">
          {supportText}
        </P>
      </div>
    </div>
  );
};
